import { useEffect, useRef } from 'react';

/**
 * Custom hook to control opacity and blur based on scroll position
 * @param {boolean} inverse - If true, opacity goes from 0 to 1 (instead of 1 to 0)
 * @param {number} deadzone - Distance from top/bottom where no changes occur
 * @param {number} threshold - Distance over which transition occurs
 * @param {number|null} overrideOpacity - If provided, this opacity value will be used instead of scroll calculation
 * @param {Object} blurOptions - Optional blur configuration { enabled: boolean, maxBlur: number, inverse: boolean }
 * @param {boolean} disableOpacity - If true, opacity changes are disabled
 * @param {Object} saturateOptions - Optional saturate configuration { enabled: boolean, maxSaturate: number, inverse: boolean }
 * @returns {ref} - React ref to attach to the element
 */
const useScrollOpacity = (
  inverse = false, 
  deadzone = 250, 
  threshold = 250, 
  overrideOpacity = null,
  blurOptions = { enabled: false, maxBlur: 50, inverse: false },
  disableOpacity = false,
  saturateOptions = { enabled: false, maxSaturate: 50, inverse: false }
) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const element = elementRef.current;
      if (!element) return;

      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY || window.pageYOffset;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Calculate how far we can scroll
      const maxScroll = documentHeight - viewportHeight;
      
      // Distance from top of scroll (current scroll position)
      const distanceFromTop = scrollY;
      
      // Distance from bottom of scroll (remaining scroll distance)
      const distanceFromBottom = maxScroll - scrollY;
      
      // Find minimum distance to nearest scroll edge
      const minDistance = Math.min(distanceFromTop, distanceFromBottom);
      
      // Calculate progress for transitions
      let progress = 0;
      if (minDistance <= deadzone) {
        progress = 0;
      } else if (minDistance <= deadzone + threshold) {
        const transitionDistance = minDistance - deadzone;
        progress = transitionDistance / threshold;
      } else {
        progress = 1;
      }

      // Handle opacity (only if not disabled)
      if (!disableOpacity) {
        if (overrideOpacity !== null) {
          element.style.opacity = overrideOpacity;
        } else {
          let opacity = inverse ? progress : 1 - progress;
          element.style.opacity = opacity;
        }
      }

      // Build filter string
      let filters = [];
      
      // Handle blur if enabled
      if (blurOptions.enabled) {
        const blurProgress = blurOptions.inverse ? progress : 1 - progress;
        const blur = blurProgress * blurOptions.maxBlur;
        filters.push(`blur(${blur}px)`);
      }
      
      // Handle saturate if enabled
      if (saturateOptions.enabled) {
        const saturateProgress = saturateOptions.inverse ? progress : 1 - progress;
        // Calculate saturate with range (e.g., from 150% to 100%)
        const saturateRange = saturateOptions.maxSaturate - (saturateOptions.minSaturate || 0);
        const saturate = (saturateOptions.minSaturate || 0) + (saturateProgress * saturateRange);
        filters.push(`saturate(${saturate}%)`);
      }
      
      // Apply combined filters or remove filter if none
      if (filters.length > 0) {
        element.style.filter = filters.join(' ');
      } else {
        element.style.filter = '';
      }
    };

    // Listen to scroll and resize events
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [inverse, deadzone, threshold, overrideOpacity, blurOptions, disableOpacity, saturateOptions]);

  return elementRef;
};

export default useScrollOpacity;
